import React from 'react';
import { Card, Text, Button, Table, StatusChip } from '@fragments';
import {
    IconBuildingBank,
    IconClockDollar,
    IconCoin,
    IconCommand,
    IconSearch,
    IconTrendingUp,
} from '@tabler/icons-react';
import './styles.scss';

// ── Mock data ───────────────────────────────────────────────────────────────
// A faithful rebuild of the AlignUI finance dashboard "Recent Transactions"
// table. Verbatim content: the five rows, their amounts, accounts, the single
// shared date, and the payment-method words. Currency is USD.
//
// `kind` drives the leading chip: people ('person') get an initials avatar,
// flows get a tinted icon chip. `debit` flips the amount to a leading
// minus (U+2212) in strong ink — we never paint amounts green/red here.
const TRANSACTIONS = [
    {
        id: 'tx-investment-return',
        name: 'Investment Return',
        kind: 'flow',
        icon: IconTrendingUp,
        amount: '$560.00',
        debit: false,
        account: 'Checking',
        date: 'September 12, 2024',
        method: 'Wire',
    },
    {
        id: 'tx-james-brown',
        name: 'James Brown',
        kind: 'person',
        amount: '−$35.20',
        debit: true,
        account: 'Ops Payroll',
        date: 'September 12, 2024',
        method: 'Money Transfer',
    },
    {
        id: 'tx-stock-dividend',
        name: 'Stock Dividend',
        kind: 'flow',
        icon: IconCoin,
        amount: '$1,250.00',
        debit: false,
        account: 'AP',
        date: 'September 12, 2024',
        method: 'ACH',
    },
    {
        id: 'tx-sophia-williams',
        name: 'Sophia Williams',
        kind: 'person',
        amount: '$150.00',
        debit: false,
        account: 'Checking',
        date: 'September 12, 2024',
        method: 'Money Transfer',
    },
    {
        id: 'tx-freelance-income',
        name: 'Freelance Income',
        kind: 'flow',
        icon: IconBuildingBank,
        amount: '$250.00',
        debit: false,
        account: 'Checking',
        date: 'September 12, 2024',
        method: 'ACH',
    },
];

// Initials for the avatar chip — first letter of the first two words.
const initials = (name) =>
    name
        .split(' ')
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase();

// ── Columns ─────────────────────────────────────────────────────────────────
// Each cell uses customRender so we control the exact glyph/contrast. The Table
// fragment supplies the muted column headers, the full-width hairline dividers
// between rows (last row borderless) and the comfortable row padding.
const COLUMNS = [
    {
        title: 'To / From',
        field: 'name',
        grow: 1,
        minWidth: 200,
        customRender: (tx) => {
            const Icon = tx.icon;
            return (
                <div className="rec-tx-tbl__party">
                    {tx.kind === 'person' ? (
                        <span className="rec-tx-tbl__avatar" aria-hidden="true">
                            <Text.caption contrast={9} weight={500}>
                                {initials(tx.name)}
                            </Text.caption>
                        </span>
                    ) : (
                        <span className="rec-tx-tbl__chip" aria-hidden="true">
                            <Icon />
                        </span>
                    )}
                    <Text.span contrast={9} maxLines={1}>
                        {tx.name}
                    </Text.span>
                </div>
            );
        },
    },
    {
        title: 'Amount',
        field: 'amount',
        minWidth: 120,
        customRender: (tx) => (
            <Text.span contrast={9} maxLines={1}>
                {tx.amount}
            </Text.span>
        ),
    },
    {
        title: 'Account',
        field: 'account',
        minWidth: 130,
        customRender: (tx) => (
            <Text.span contrast={6} maxLines={1}>
                {tx.account}
            </Text.span>
        ),
    },
    {
        title: 'Date & Time',
        field: 'date',
        minWidth: 160,
        customRender: (tx) => (
            <Text.caption contrast={5} maxLines={1}>
                {tx.date}
            </Text.caption>
        ),
    },
    {
        title: 'Payment Method',
        field: 'method',
        minWidth: 150,
        customRender: (tx) => (
            <StatusChip.Default text={tx.method} preserveCase />
        ),
    },
];

/**
 * RecentTransactionsTable — the AlignUI "Recent Transactions" table card.
 * A flat white card on the gray canvas: header (title + subtitle + "See All"),
 * flush body that owns its inset, holding our Table fragment.
 */
export default function RecentTransactionsTable() {
    return (
        <Card
            flat
            noBodyPadding
            title="Recent Transactions"
            subtitle="Display the recent transactions in the table below."
            icon={<IconClockDollar />}
            action={
                <div className="rec-tx-tbl__toolbar">
                    <div className="rec-tx-tbl__search">
                        <IconSearch />
                        <Text.span contrast={6}>Search...</Text.span>
                        <span><IconCommand />1</span>
                    </div>
                    <Button color="inverse" variant="text" size="small">
                        See All
                    </Button>
                </div>
            }
        >
            <div className="rec-tx-tbl">
                <Table
                    contained
                    draggable={false}
                    columns={COLUMNS}
                    data={TRANSACTIONS}
                />
            </div>
        </Card>
    );
}
